import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Slide,
  SlideProps,
  Typography
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CloseIcon from "@mui/icons-material/Close";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { SearchParameters } from "@/types/dto/search-filters/search-parameters";
import SearchFilters from "./filter-layout";
import SortOrder from "./sort-order";
import searchParamKeys from "./search-param-keys";


const Transition = React.forwardRef((
  props: SlideProps,
  ref: React.Ref<unknown>
) => <Slide direction="up" ref={ref} {...props} />);


type FilterControlsProps = {
  filtersData: any;
  rangesData: any;
  sortOptions: any;
  searchFilters: Partial<SearchParameters>;
  handleFilterChange: (filters: any) => void;
  handleClearAll: () => void;
  handleSortChange: (sortOrder: string) => void;
  isLoading: boolean;
  filterDialogOpen: boolean;
  sortDialogOpen: boolean;
  totalTours: number;
  setFilterDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSortDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onFilterInteraction?: (filterType: string, filterValue: string | string[], action: "add" | "remove" | "clear" | "sort") => void;
};

export default function FilterControls({
  filtersData,
  rangesData,
  searchFilters,
  handleFilterChange,
  handleClearAll,
  handleSortChange,
  isLoading,
  filterDialogOpen,
  sortDialogOpen,
  totalTours,
  sortOptions,
  setFilterDialogOpen,
  setSortDialogOpen,
  onFilterInteraction
}: FilterControlsProps) {
  // State to track temporary sort selection for mobile
  const [tempSortOrder, setTempSortOrder] = useState<string>("");

  // Initialize temp sort order when sort dialog opens or when current sort changes
  useEffect(() => {
    if (sortDialogOpen) {
      setTempSortOrder(searchFilters.sortBy || "");
    }
  }, [sortDialogOpen, searchFilters.sortBy]);

  // Only include searchFilters whose key is in searchParamKeys
  const filteredSearchParams = Object.keys(searchFilters)
    .filter(key => searchParamKeys.has(key as keyof SearchParameters))
    .map(key => searchFilters[key as keyof SearchParameters]);
  const filterCount = Object.values(filteredSearchParams).filter(Boolean).length;

  // Handler for mobile sort changes (doesn't apply immediately)
  const handleMobileSortChange = (sortOrder: string) => {
    setTempSortOrder(sortOrder);
  };

  // Handler for applying mobile sort changes
  const handleApplySortChanges = () => {
    if (tempSortOrder !== searchFilters.sortBy) {
      handleSortChange(tempSortOrder);
    }
    setSortDialogOpen(false);
  };

  // Handler for canceling mobile sort changes
  const handleCancelSortChanges = () => {
    setTempSortOrder(searchFilters.sortBy || "");
    setSortDialogOpen(false);
  };
  return (
    <>
      <Box sx={{ flexDirection: "row", display: "flex" }} data-testid="mobile-filter-sort-buttons">
        <ColletteButton
          color="info"
          label="Filters"
          sx={{
            backgroundColor: "background.default",
            fontSize: "1.4rem!important",
            mr: "1.9rem",
            "&:hover, &:focus-visible": {
              backgroundColor: "divider"
            }
          }}
          startIcon={<TuneIcon />}
          endIcon={`(${filterCount})`}
          onClick={() => setFilterDialogOpen(true)}
          testId="mobile-filter-button"
        />
        <ColletteButton
          color="info"
          label="Sort"
          sx={{
            backgroundColor: "background.default",
            fontSize: "1.4rem!important",
            "&:hover, &:focus-visible": {
              backgroundColor: "divider"
            }
          }}
          startIcon={<SwapVertIcon />}
          onClick={() => setSortDialogOpen(true)}
          testId="mobile-sort-button"
        />
      </Box>

      <Dialog
        fullScreen
        sx={{
          display: { xs: "flex", legacyDesktop: "none" }
        }}
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        slots={{ transition: Transition }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "background.default"
            }
          }
        }}
        data-testid="mobile-filter-dialog"
      >
        {/* Embed your SearchFilters component here */}
        <SearchFilters
          filters={filtersData}
          ranges={rangesData}
          currentSearchParams={searchFilters}
          onFiltersChange={handleFilterChange}
          onClearAll={handleClearAll}
          onFilterInteraction={onFilterInteraction}
          isLoading={isLoading}
          onClose={() => setFilterDialogOpen(false)}
        />
        <Box
          sx={{
            display: { xs: "flex", legacyDesktop: "none" },
            flexDirection: "row",
            justifyContent: "center",
            pt: "4rem",
            alignItems: "center",
            gap: "1.6rem",
            pb: "4rem",
            position: "sticky",
            bottom: 0,
            height: "30px",
            width: "100vw",
            boxShadow: { xs: 8, legacyDesktop: 0 },
            backgroundColor: "background.default"
          }}
          id="mobile-search-footer"
          data-testid="mobile-search-footer">
          <ColletteButton
            color="info"
            onClick={handleClearAll}
            label="Clear All"
            testId="clear-all-button-mobile"
            sx={{
              backgroundColor: "background.default",
              display: { xs: "flex", legacyDesktop: "none" },
              padding: "1rem 2.4rem",
              gap: "8px",
              "&:hover, &:focus-visible": {
                backgroundColor: "divider"
              }
            }} />

          <ColletteButton
            component="a"
            color="secondary"
            label={`Show ${totalTours} Tours`}
            testId="mobile-view-tour-count-button"
            href="#"
            onClick={() => setFilterDialogOpen(false)}
            sx={{
              whiteSpace: "nowrap",
              display: "flex",
              "&:focus-visible": {
                backgroundColor: "secondary.dark"
              }
            }}
          />
        </Box>
      </Dialog>
      <Dialog
        fullScreen
        sx={{ display: { xs: "flex", legacyDesktop: "none" } }}
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        slots={{ transition: Transition }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "background.default"
            }
          }
        }}
        data-testid="mobile-sort-dialog"
      >
        <Box
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            display: "flex",
            p: "2.4rem"
          }}
          data-testid="sort-dialog-header"
        >
          <Typography sx={{
            color: "text.primary",
            fontSize: "24px",
            fontWeight: 500
          }} data-testid="sort-dialog-title">
            Sort
          </Typography>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setSortDialogOpen(false)}
            aria-label="close"
            sx={{ display: { xs: "flex", legacyDesktop: "none" } }}
            data-testid="sort-dialog-close-button"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: "2.4rem", pt: 0, height: "90%" }} data-testid="sort-dialog-content">
          <SortOrder
            sortOptions={sortOptions}
            onSortChange={handleMobileSortChange}
            currentSortOrder={tempSortOrder}
            onFilterInteraction={onFilterInteraction}
          />
        </Box>
        <Box
          sx={{
            display: { xs: "flex", legacyDesktop: "none" },
            flexDirection: "row",
            justifyContent: "center",
            pt: "4rem",
            alignItems: "center",
            gap: "1.6rem",
            pb: "4rem",
            position: "sticky",
            bottom: 0,
            height: "30px",
            width: "100vw",
            boxShadow: { xs: 8, legacyDesktop: 0 },
            backgroundColor: "background.default"
          }}
          id="mobile-search-footer"
          data-testid="mobile-search-footer">
          <ColletteButton
            color="info"
            onClick={handleCancelSortChanges}
            label="Cancel"
            testId="cancel-all-button-mobile"
            sx={{
              backgroundColor: "background.default",
              display: { xs: "flex", legacyDesktop: "none" },
              padding: "1rem 2.4rem",
              gap: "8px",
              "&:hover, &:focus-visible": {
                backgroundColor: "divider"
              }
            }} />

          <ColletteButton
            component="a"
            color="secondary"
            label="Apply"
            testId="mobile-sort-tour-apply-button"
            href="#"
            onClick={handleApplySortChanges}
            sx={{
              whiteSpace: "nowrap",
              width: "50%",
              "&:focus-visible": {
                backgroundColor: "secondary.dark"
              }
            }}
          />
        </Box>
      </Dialog>
    </>
  );
}